
import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar,
    PieChart, Pie, Cell, Legend
} from 'recharts';

export default function Analytics({ isDarkMode }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            const res = await api.get('/restaurant/analytics');
            setData(res.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const styles = getStyles(isDarkMode);

    if (loading) return <div style={{ padding: '2rem', color: isDarkMode ? '#fff' : '#333' }}>Loading Analytics...</div>;
    if (!data) return <div style={{ padding: '2rem', color: isDarkMode ? '#fff' : '#333' }}>Failed to load data</div>;

    const { stats, charts } = data;

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

    return (
        <div style={styles.container}>
            <h2 style={styles.title}>Dashboard Analytics</h2>

            {/* Stats Cards */}
            <div style={styles.statsGrid}>
                <div style={styles.card}>
                    <div style={styles.cardTitle}>Today's Orders</div>
                    <div style={styles.cardValue}>{stats.totalOrdersToday}</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardTitle}>Today's Revenue</div>
                    <div style={{ ...styles.cardValue, color: '#00b894' }}>
                        ₹{new Intl.NumberFormat('en-IN').format(stats.totalRevenueToday)}
                    </div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardTitle}>Pending Orders</div>
                    <div style={{ ...styles.cardValue, color: '#e17055' }}>{stats.pendingOrders}</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardTitle}>Best Seller</div>
                    <div style={{ ...styles.cardValue, fontSize: '1.2rem', marginTop: 'auto', marginBottom: 'auto' }}>
                        {stats.mostSellingItem}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#888' }}>{stats.mostSellingItemCount} sold (30d)</div>
                </div>
            </div>

            {/* Charts Grid */}
            <div style={styles.chartsGrid}>
                {/* Daily Sales Chart */}
                <div style={styles.chartCard}>
                    <h3 style={styles.chartTitle}>Daily Sales (7 Days)</h3>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <LineChart data={charts.dailySales}>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#444' : '#eee'} />
                                <XAxis dataKey="date" stroke={isDarkMode ? '#aaa' : '#666'} />
                                <YAxis stroke={isDarkMode ? '#aaa' : '#666'} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: isDarkMode ? '#333' : '#fff', border: 'none', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}
                                />
                                <Line type="monotone" dataKey="sales" stroke="#6C5CE7" strokeWidth={3} dot={{ r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Weekly Orders Chart */}
                <div style={styles.chartCard}>
                    <h3 style={styles.chartTitle}>Weekly Orders</h3>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <BarChart data={charts.weeklyOrders}>
                                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#444' : '#eee'} />
                                <XAxis dataKey="date" stroke={isDarkMode ? '#aaa' : '#666'} />
                                <YAxis stroke={isDarkMode ? '#aaa' : '#666'} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: isDarkMode ? '#333' : '#fff', border: 'none', borderRadius: '8px' }}
                                />
                                <Bar dataKey="orders" fill="#00b894" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Top Items Pie */}
                <div style={styles.chartCard}>
                    <h3 style={styles.chartTitle}>Top Items</h3>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={charts.topItems}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {charts.topItems.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
}

const getStyles = (isDark) => ({
    container: {
        padding: '24px 32px',
        color: isDark ? '#fff' : '#2D3436'
    },
    title: {
        fontSize: '1.8rem',
        fontWeight: '700',
        marginBottom: '24px'
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
    },
    card: {
        backgroundColor: isDark ? '#1e1e1e' : '#fff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: isDark ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        border: isDark ? '1px solid #333' : '1px solid #f0f0f0'
    },
    cardTitle: {
        fontSize: '0.9rem',
        color: isDark ? '#aaa' : '#636e72',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '10px'
    },
    cardValue: {
        fontSize: '2rem',
        fontWeight: '700',
        color: isDark ? '#fff' : '#2D3436'
    },
    chartsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '24px'
    },
    chartCard: {
        backgroundColor: isDark ? '#1e1e1e' : '#fff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: isDark ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)',
        border: isDark ? '1px solid #333' : '1px solid #f0f0f0',
        minHeight: '350px'
    },
    chartTitle: {
        marginBottom: '20px',
        fontSize: '1.1rem',
        fontWeight: '600',
        color: isDark ? '#ddd' : '#2d3436'
    }
});
